import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Database, ChevronRight, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { DocumentClass, DocumentClassSource } from '../../api/apiTypes';
import { fetchDocumentSourcesByClass } from '../../api/functionApi';
import DocumentSourcesModal from '../banks/dettaglio-Banca/DocumentSourcesModal';
import { labelMapping } from '../../utils/documentLabels';

interface ContainerAltreFontiPageProps {
  documents: DocumentClass[];
}

const ContainerAltreFontiPage: React.FC<ContainerAltreFontiPageProps> = ({ documents }) => {
  const [openDocsModal, setOpenDocsModal] = useState<string | null>(null);
  const [docSources, setDocSources] = useState<Record<string, DocumentClassSource[]>>({});
  const [fontiCounts, setFontiCounts] = useState<Record<string, number>>({});

  const loadSources = useCallback(async () => {
    const counts: Record<string, number> = {};
    const sourcesMap: Record<string, DocumentClassSource[]> = {};

    for (const doc of documents) {
      try {
        const sources = await fetchDocumentSourcesByClass(doc.id);
        const activeSources = sources.filter(src => src.is_active);
        counts[doc.document_type] = activeSources.length;
        sourcesMap[doc.document_type] = sources;
      } catch (error) {
        console.error(`Errore caricando fonti per ${doc.document_type}`, error);
        counts[doc.document_type] = 0;
        sourcesMap[doc.document_type] = [];
      }
    }

    setFontiCounts(counts);
    setDocSources(sourcesMap);
  }, [documents]);

  useEffect(() => {
    loadSources();
  }, [loadSources]);

  // Una classe obbligatoria senza fonti blocca la creazione del report: va vista
  // a colpo d'occhio, non nascosta in fondo alla scheda.
  const scoperte = documents.filter(
    d => d.is_required && (fontiCounts[d.document_type] ?? 0) === 0,
  ).length;

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold font-heading text-textPrimary">Fonti</h2>
          <p className="text-textSecondary mt-1 text-sm font-paragraph">
            Seleziona una fonte per gestirne i dettagli.
          </p>
        </div>

        {documents.length > 0 && (
          <span
            className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[11px] font-medium ${
              scoperte > 0
                ? 'bg-amber-50 text-amber-700 border-amber-200'
                : 'bg-green-50 text-green-700 border-green-200'
            }`}
          >
            {scoperte > 0 ? (
              <>
                <AlertTriangle className="w-3.5 h-3.5" />
                {scoperte} {scoperte === 1 ? 'classe obbligatoria scoperta' : 'classi obbligatorie scoperte'}
              </>
            ) : (
              <>
                <CheckCircle2 className="w-3.5 h-3.5" />
                Tutte le classi obbligatorie hanno una fonte
              </>
            )}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {documents.map((doc, idx) => {
          const mapping = labelMapping[doc.document_type] || {
            label: doc.document_type,
            description: doc.description || '',
          };
          const count = fontiCounts[doc.document_type] ?? 0;
          const scoperta = doc.is_required && count === 0;

          return (
            <motion.button
              key={doc.document_type}
              type="button"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.04 }}
              onClick={() => setOpenDocsModal(doc.document_type)}
              className={`group text-left p-4 rounded-xl border bg-white transition-all hover:shadow-md ${
                scoperta
                  ? 'border-amber-200 bg-amber-50/30 hover:border-amber-300'
                  : 'border-borderDivider hover:border-textPrimary/20'
              }`}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                    scoperta ? 'bg-amber-100' : count > 0 ? 'bg-green-100' : 'bg-gray-50'
                  }`}
                >
                  {scoperta ? (
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                  ) : count > 0 ? (
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                  ) : (
                    <Database className="w-4 h-4 text-textPrimary/50" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm font-semibold text-textPrimary leading-snug">
                      {mapping.label}
                    </h3>
                    <ChevronRight className="w-4 h-4 text-textMuted shrink-0 group-hover:text-textPrimary transition-colors" />
                  </div>

                  <div className="flex items-center gap-1.5 mt-1.5">
                    <span
                      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${
                        scoperta
                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : 'bg-gray-50 text-textSecondary border-borderDivider'
                      }`}
                    >
                      {count} {count === 1 ? 'fonte' : 'fonti'}
                    </span>
                    {doc.is_required && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-50 text-violet-500 border border-violet-100">
                        obbligatoria
                      </span>
                    )}
                  </div>

                  <p className="text-[11px] text-textMuted leading-relaxed mt-2">
                    {mapping.description}
                  </p>
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>

      {openDocsModal && (
        <DocumentSourcesModal
          openDocsModal={openDocsModal}
          setOpenDocsModal={setOpenDocsModal}
          documents={documents}
          docSources={docSources}
          setDocSources={setDocSources}
          fontiCounts={fontiCounts}
          setFontiCounts={setFontiCounts}
          reloadSources={loadSources}
        />
      )}
    </div>
  );
};

export default ContainerAltreFontiPage;
